/**
 * The repair spray. This is the PowerWash layer and the single most important continuous
 * sound in the game.
 *
 * THE DESIGN RULE: you must be able to hear the fill meter climb without looking at it.
 * Four things move together with fill01, all in the same direction, so the rise is
 * unmistakable even under music and engine:
 *
 *   1. the broadband hiss band centre    820 Hz → 3100 Hz
 *   2. a resonant peak riding on it     1450 Hz → 4200 Hz
 *   3. a quiet tonal drone               172 Hz →  402 Hz   (a major tenth — a real interval,
 *                                                            so the rise is musical, not just
 *                                                            a slide)
 *   4. the drone's own lowpass           500 Hz → 1400 Hz
 *
 * On top of that a 7.5 Hz pump throb (the compressor cycling) keeps it alive, and above 90 %
 * an extra push in level makes the last stretch feel like effort. Milestone ticks and the
 * completion chime are one-shots and live in the bank, not here.
 */

import { AUDIO_TUNING as T } from './tuning.js';

const S = T.spray;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const lerp = (a, b, t) => a + (b - a) * t;

export class SprayVoice {
  constructor(graph) {
    this.graph = graph;
    this.built = false;
    this.active = false;
    this.fill01 = 0;
    this.level = 0;        // smoothed 0..1 envelope, tracked with or without audio
    this.enabled = true;
  }

  build() {
    const g = this.graph;
    if (!g.live || this.built) return;
    const a = g.actx;
    const now = a.currentTime;

    this.out = a.createGain();
    this.out.gain.value = 0.0001;
    this.out.connect(g.bus.sfx);

    // Pump throb rides on the output gain.
    this.throbOsc = a.createOscillator();
    this.throbOsc.type = 'sine';
    this.throbOsc.frequency.value = S.throbHz;
    this.throbDepth = a.createGain();
    this.throbDepth.gain.value = 0;
    this.throbOsc.connect(this.throbDepth);
    this.throbDepth.connect(this.out.gain);
    this.throbOsc.start(now);

    // ---- 1 & 2: hiss + resonant peak
    this.hissSrc = g.makeNoiseSource('white');
    this.band = a.createBiquadFilter();
    this.band.type = 'bandpass';
    this.band.frequency.value = S.bandHzAt0;
    this.band.Q.value = S.bandQ;
    this.peak = a.createBiquadFilter();
    this.peak.type = 'peaking';
    this.peak.frequency.value = S.peakHzAt0;
    this.peak.Q.value = S.peakQ;
    this.peak.gain.value = S.peakDb;
    this.hissGain = a.createGain();
    // Makeup for the narrow bandpass — see AUDIO_TUNING.spray.bandMakeup.
    this.hissGain.gain.value = S.bandMakeup;
    this.hissSrc.connect(this.band); this.band.connect(this.peak);
    this.peak.connect(this.hissGain); this.hissGain.connect(this.out);

    // ---- 3 & 4: the tonal drone
    this.tone = a.createOscillator();
    this.tone.type = S.toneType;
    this.tone.frequency.value = S.toneHzAt0;
    this.toneLp = a.createBiquadFilter();
    this.toneLp.type = 'lowpass';
    this.toneLp.frequency.value = S.toneLpHzAt0;
    this.toneLp.Q.value = S.toneLpQ;
    this.toneGain = a.createGain();
    this.toneGain.gain.value = S.toneGain;
    this.tone.connect(this.toneLp); this.toneLp.connect(this.toneGain);
    this.toneGain.connect(this.out);
    this.tone.start(now);

    // ---- sizzle: hot bitumen meeting cold road
    this.sizSrc = g.makeNoiseSource('white');
    this.sizSrc.playbackRate.value = 1.17;
    this.sizHp = a.createBiquadFilter();
    this.sizHp.type = 'highpass';
    this.sizHp.frequency.value = S.sizzleHpHz;
    this.sizLp = a.createBiquadFilter();
    this.sizLp.type = 'lowpass';
    this.sizLp.frequency.value = S.sizzleLpHz;
    this.sizGain = a.createGain();
    this.sizGain.gain.value = S.sizzleGain;
    this.sizSrc.connect(this.sizHp); this.sizHp.connect(this.sizLp);
    this.sizLp.connect(this.sizGain); this.sizGain.connect(this.out);

    this.built = true;
  }

  /** Called every presentation frame from the gameplay query. */
  set(active, fill01) {
    this.active = !!active;
    this.fill01 = clamp01(fill01 || 0);
  }

  update(dt) {
    // Envelope tracked regardless of audio so harnessStats reports the real state.
    const target = this.active ? 1 : 0;
    const tau = this.active ? S.attack : S.release;
    const k = 1 - Math.exp(-dt / Math.max(0.001, tau));
    this.level += (target - this.level) * k;
    if (this.level < 1e-4) this.level = 0;

    if (!this.graph.live || !this.built || !this.enabled) return;

    const g = this.graph;
    const gl = S.paramGlide;
    const f = this.fill01;

    g.param(this.band.frequency, lerp(S.bandHzAt0, S.bandHzAt1, f), gl);
    g.param(this.peak.frequency, lerp(S.peakHzAt0, S.peakHzAt1, f), gl);
    g.param(this.tone.frequency, lerp(S.toneHzAt0, S.toneHzAt1, f), gl);
    g.param(this.toneLp.frequency, lerp(S.toneLpHzAt0, S.toneLpHzAt1, f), gl);
    g.param(this.sizGain.gain, lerp(S.sizzleGain, S.sizzleGainAt1, f), gl);

    // The last push: above 90 % it leans in.
    const push = f > S.lastPushFrom ? S.lastPushGain * ((f - S.lastPushFrom) / (1 - S.lastPushFrom)) : 0;
    const lvl = (S.gain + push) * this.level;
    g.param(this.out.gain, 0.0001 + lvl, 0.02);
    g.param(this.throbDepth.gain, S.throbDepth * lvl, 0.05);
  }

  setEnabled(on) {
    this.enabled = on;
    if (this.graph.live && this.built && !on) this.graph.param(this.out.gain, 0.0001, 0.03);
  }

  reset() { this.active = false; this.fill01 = 0; this.level = 0; }

  stats() {
    return { active: this.active, fill01: +this.fill01.toFixed(3), level: +this.level.toFixed(3) };
  }
}
